import { useState } from "react";

import Badge from "react-bootstrap/Badge";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Popover from "react-bootstrap/Popover";
import Table from "react-bootstrap/Table";
import { FaEllipsisV, FaInfoCircle } from "react-icons/fa";

import { handleColumnToggle } from "@/utils/handleColumnTogle";
import { ResumeIndicatorM1ByHealthTeamData } from "@/services/esus/indicator/emulti/m1/getResumeIndicatorM1ByHealthTeam";
import {
	getIndicatorClassification,
	getIndicatorClassificationColorClassName,
	getIndicatorClassificationScores,
	IndicatorEmultiEnum,
} from "@/utils/indicatorsEmulti";
import { calculatePercentage } from "@/utils/calculatePercentage";

import { TableSkeletons } from "@/components/TableSkeletons";
import { CardOverlayTrigger } from "@/components/CardOverlayTrigger";

type Column = {
	title: string;
	visible: boolean;
};

type IndicatorEmultiM1HealthTeamsClassificationTableColumns = {
	healthTeamName: Column;
	totalAttendances: Column;
	totalCitizens: Column;
	classificationAverage: Column;
	classification: Column;
};

type IndicatorEmultiM1HealthTeamsTableProps = {
	healthTeamsClassifications: ResumeIndicatorM1ByHealthTeamData[];
	isLoading: boolean;
};

const indicatorClassificationScores = getIndicatorClassificationScores(IndicatorEmultiEnum.M1);

export function IndicatorEmultiM1HealthTeamsClassificationTable({
	healthTeamsClassifications,
	isLoading,
}: Readonly<IndicatorEmultiM1HealthTeamsTableProps>) {
	const [columns, setColumns] = useState<IndicatorEmultiM1HealthTeamsClassificationTableColumns>({
		healthTeamName: { title: "Equipe", visible: true },
		totalAttendances: { title: "Atendimentos", visible: true },
		totalCitizens: { title: "Cidadãos", visible: true },
		classificationAverage: { title: "Média de Atendimentos por Cidadão", visible: true },
		classification: { title: "Classificação", visible: true },
	});

	return (
		<Table striped bordered hover responsive>
			<thead className="text-center fs-7">
				<tr className="fw-bolder text-center">
					<th
						className={`text-start px-2 ${!columns.healthTeamName.visible && "d-none"}`}
					>
						{columns.healthTeamName.title}
					</th>
					<th className={`px-2 ${!columns.totalAttendances.visible && "d-none"}`}>
						{columns.totalAttendances.title}{" "}
						<CardOverlayTrigger
							header="Total de atendimentos"
							message="Quantidade de atendimentos e atividades coletivas realizadas pela emulti nos últimos 12 meses"
							icon={<FaInfoCircle />}
						/>
					</th>
					<th className={`px-2 ${!columns.totalCitizens.visible && "d-none"}`}>
						{columns.totalCitizens.title}{" "}
						<CardOverlayTrigger
							header="Total de cidadãos"
							message="Quantidade de cidadãos distintos atendidos por emulti nos últimos 12 meses"
							icon={<FaInfoCircle />}
						/>
					</th>
					<th className={`px-2 ${!columns.classificationAverage.visible && "d-none"}`}>
						{columns.classificationAverage.title}
					</th>
					<th className={`px-2 ${!columns.classification.visible && "d-none"}`}>
						{columns.classification.title}{" "}
						<CardOverlayTrigger header={"Classificação"} icon={<FaInfoCircle />}>
							<div className="text-start">
								<div className="text-primary">{`Ótimo: > ${indicatorClassificationScores.awesome}`}</div>
								<br />
								<div className="text-success">{`Bom: > ${indicatorClassificationScores.good} e <= ${indicatorClassificationScores.awesome}`}</div>
								<br />
								<div className="text-warning">
									{`Suficiente: > ${indicatorClassificationScores.enough} e <= ${indicatorClassificationScores.good}`}
								</div>
								<br />
								<div className="text-danger">{`Regular: <= ${indicatorClassificationScores.enough}`}</div>
								<br />
							</div>
						</CardOverlayTrigger>
					</th>
					<th className="text-nowrap pb-0">
						<OverlayTrigger
							trigger="click"
							placement="bottom"
							overlay={
								<Popover>
									<Popover.Body>
										<Form className="d-flex flex-column gap-2">
											{Object.keys(columns).map((key, index) => (
												<Form.Check
													key={`citizen-attendances-table-column-${
														index + 1
													}`}
													type="checkbox"
													label={
														columns[
															key as keyof IndicatorEmultiM1HealthTeamsClassificationTableColumns
														].title
													}
													checked={
														columns[
															key as keyof IndicatorEmultiM1HealthTeamsClassificationTableColumns
														].visible
													}
													onChange={() =>
														handleColumnToggle(
															key as keyof IndicatorEmultiM1HealthTeamsClassificationTableColumns,
															setColumns
														)
													}
												/>
											))}
										</Form>
									</Popover.Body>
								</Popover>
							}
							rootClose
						>
							<Button
								title="Exibição de colunas da tabela"
								variant="white"
								className="px-2"
								disabled={isLoading}
							>
								<FaEllipsisV />
							</Button>
						</OverlayTrigger>
					</th>
				</tr>
			</thead>
			<tbody className="fs-8">
				{!isLoading && healthTeamsClassifications.length > 0
					? healthTeamsClassifications.map((healthTeamClassification, index) => (
							<tr
								className="text-center"
								key={`health-team-classification-table-row-${index + 1}`}
							>
								<td
									className={`text-start px-2 ${
										!columns.healthTeamName.visible && "d-none"
									}`}
								>
									<div className="d-flex flex-column">
										<div>{healthTeamClassification.healthTeamName}</div>
										<div className="text-muted">
											{healthTeamClassification.healthTeamIne}
										</div>
									</div>
								</td>
								<td
									className={`px-2 ${
										!columns.totalAttendances.visible && "d-none"
									}`}
								>
									{healthTeamClassification.totalAttendances ?? 0}
								</td>
								<td
									className={`px-2 ${!columns.totalCitizens.visible && "d-none"}`}
								>
									{healthTeamClassification.totalCitizens ?? 0}
								</td>
								<td
									className={`px-2 ${
										!columns.classificationAverage.visible && "d-none"
									}`}
								>
									{`${calculatePercentage(
										healthTeamClassification.totalAttendances / 100,
										healthTeamClassification.totalCitizens
									)}`}
								</td>
								<td
									className={`text-nowrap px-2 ${
										!columns.classification.visible && "d-none"
									}`}
								>
									<Badge
										bg={getIndicatorClassificationColorClassName(
											IndicatorEmultiEnum.M1,
											calculatePercentage(
												healthTeamClassification.totalAttendances / 100,
												healthTeamClassification.totalCitizens
											)
										)}
									>
										{getIndicatorClassification(
											IndicatorEmultiEnum.M1,
											calculatePercentage(
												healthTeamClassification.totalAttendances / 100,
												healthTeamClassification.totalCitizens
											)
										)}
									</Badge>
								</td>
								<td></td>
							</tr>
					  ))
					: isLoading && (
							<TableSkeletons
								numberOfCells={
									Object.values(columns).reduce(
										(count, column) =>
											count + (column?.visible === true ? 1 : 0),
										0
									) + 1
								}
							/>
					  )}
			</tbody>
		</Table>
	);
}
